import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  Renderer2,
  AfterViewInit,
  OnDestroy,
  NgZone,
  Output,
  EventEmitter,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { from, fromEvent, Observable, Subscription } from 'rxjs';
import { take, finalize, mergeMap, throttleTime } from 'rxjs/operators';

import { getServerConfig } from '@app/origami-api/server-config';
import { tiStringsFormat } from '@app/shared/pipes/string-format.pipe';
import { SessionService } from '@app/session/session.service';
import {
  AppThemeMessages,
  GlobalEventService,
  OldUiMessages,
  UserActivityMessages,
} from '@app/services/global-event.service';
import { OrigamiResponse } from '@app/origami-api/services/base/service.base';
import { getErrorDescription } from '@app/origami-api/utils/error-utils';
import { CssVariablesService } from '@app/services/css-variables.service';
import { ThemeColor, ThemeColorService } from '@app/services/theme-color.service';

@Component({
  standalone: false,
  selector: 'ti-content',
  template: `
    <ng-content></ng-content>
    <iframe #iframe *ngIf="safeUrl" [src]="safeUrl" (load)="onIframeLoad($event)"></iframe>
    <div
      *ngIf="isShowingError"
      class="message"
      [innerHTML]="tiStringsFormat(strings.errorMessage, errorMessage, supportEmail)"></div>
    <ti-spinner *ngIf="isLoading"></ti-spinner>
  `,
  styleUrls: ['./content-viewer.component.sass'],
})
export class ContentComponent implements AfterViewInit, OnDestroy, OnInit {
  strings = tiStrings.shared.contentViewer;

  @ViewChild('iframe') iframe: ElementRef;

  @Output() onLoad = new EventEmitter<any>();

  @Input() set url(url: string | Observable<string>) {
    if (url instanceof Observable) {
      this.isLoading = true;

      url
        .pipe(
          take(1),
          finalize(() => (this.isLoading = false)),
        )
        .subscribe(
          (res) => this.setSafeUrl(res),
          (err: OrigamiResponse<any>) => {
            this.supportEmail = getServerConfig().supportEmail;
            this.errorMessage = getErrorDescription(err.ErrorCode) || '';
            this.isShowingError = true;
          },
        );
    } else {
      setTimeout(() => this.setSafeUrl(url));
    }
  }

  @Input() syncLocationHash: boolean;

  @Input() syncAppTheme: boolean;

  @Output() locationHashChange: EventEmitter<string> = new EventEmitter<string>();

  safeUrl: SafeResourceUrl;
  isLoading = false;
  isShowingError = false;
  errorMessage: string;
  supportEmail: string;
  tiStringsFormat = tiStringsFormat;

  private iframeEventsSub$: Subscription = new Subscription();
  private monitorLocationSub$: Subscription;
  private monitorAppTheme$: Subscription;

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly sessionService: SessionService,
    private readonly globalEventService: GlobalEventService,
    private readonly renderer: Renderer2,
    private readonly cd: ChangeDetectorRef,
    private readonly zone: NgZone,
    private readonly cssVariablesService: CssVariablesService,
    private readonly themeColorService: ThemeColorService,
  ) {}

  ngOnInit() {
    if (this.syncLocationHash) {
      this.setupLocationObserver();
    }

    if (this.syncAppTheme) {
      this.setupAppThemeObserver();
    }
  }

  ngAfterViewInit() {
    this.setHtmlFontSize(this.sessionService.userZoom.default);

    this.sessionService.userZoomObs.subscribe((userZoom) => {
      this.setHtmlFontSize(userZoom.default);
    });
  }

  ngOnDestroy(): void {
    this.iframeEventsSub$ && this.iframeEventsSub$.unsubscribe();
    this.monitorLocationSub$ && this.monitorLocationSub$.unsubscribe();
    this.monitorAppTheme$ && this.monitorAppTheme$.unsubscribe();
  }

  private listenToIframeEvents() {
    if (!this.iframe || !this.iframe.nativeElement || !this.iframe.nativeElement.contentDocument) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.iframeEventsSub$ && this.iframeEventsSub$.unsubscribe();

      this.iframeEventsSub$.add(
        from(['mousemove', 'keydown'])
          .pipe(
            mergeMap((event) => fromEvent(this.iframe.nativeElement.contentDocument, event)),
            throttleTime(1000),
          )
          .subscribe((event) => {
            this.globalEventService.broadcast(UserActivityMessages.iframeActivity, UserActivityMessages.iframeActivity);
          }),
      );
    });
  }

  setHtmlFontSize(percentage) {
    if (!this.iframe || !this.iframe.nativeElement) {
      return;
    }

    const content: HTMLElement = this.iframe.nativeElement;

    // content.

    const width = 100 * (10 / (percentage * 10));
    const height = 100 * (10 / (percentage * 10));
    this.renderer.setStyle(content, 'transform', `scale(${percentage})`);
    this.renderer.setStyle(content, 'width', `${width}%`);
    this.renderer.setStyle(content, 'height', `${height}%`);

    const offsetwidth = (100 - width) / 2;
    const offsetHeight = (100 - height) / 2;

    this.renderer.setStyle(content, 'position', 'relative');
    this.renderer.setStyle(content, 'left', `${offsetwidth}%`);
    this.renderer.setStyle(content, 'top', `${offsetHeight}%`);
    // this.renderer.setProperty(document.documentElement, 'style', `font-size: ${HTML_FONT_SIZE * percentage}px !important`);
  }

  onIframeLoad(e) {
    this.onLoad.emit(e);

    if (this.syncAppTheme) {
      this.doSyncAppTheme(e.target.contentDocument, e.target.contentWindow, this.themeColorService.getThemeColor());
    }

    setTimeout(() => this.listenToIframeEvents(), 1000);
  }

  doSyncAppTheme(doc, win, themeColor?: ThemeColor) {
    ///
    // TODO: temporary switched off until we fix the dark mode
    ///
    //this.cssVariablesService.injectAllCssVarsToDocument(doc, win);
    //doc.documentElement.setAttribute('theme', `${themeColor}`);
  }

  private setSafeUrl(url: string) {
    this.safeUrl = url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
    this.cd.detectChanges();
  }

  private setupLocationObserver() {
    this.monitorLocationSub$ = this.globalEventService
      .on(OldUiMessages.hashChange)
      .subscribe((locationHash: string) => {
        this.locationHashChange.emit(locationHash);
      });
  }

  private setupAppThemeObserver() {
    this.monitorAppTheme$ = this.globalEventService
      .on(AppThemeMessages.themeChanged)
      .subscribe((themeColor: ThemeColor) => {
        this.doSyncAppTheme(
          this.iframe.nativeElement.contentDocument,
          this.iframe.nativeElement.contentWindow,
          themeColor,
        );
      });
  }
}
